import {
    ConjunctionalAgencySchema,
    InternalConjunctionalAgencySchema,
} from '@buyerflo/core/schemas/conjunctionalAgency'
import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Trash2 } from '@tamagui/lucide-icons-2'
import { useMutation } from '@tanstack/react-query'
import { useConjunctionalAgenciesApi, useUserName, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import { Autocomplete, SlotAutocomplete } from 'components/autocomplete'
import Button from 'components/button'
import { Drawer } from 'components/drawer'
import { SlotInput, SlotWrapper } from 'components/input'
import { RadioChips } from '@buyerflo/ui/components/RadioChips'
import { useShowSnack } from 'components/snackbar'
import { prop, without } from 'ramda'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useDebounce } from 'use-debounce'
import { useTRPC } from 'utils/trpc'

interface XeroContact {
    id: string
    name: string
}

const XeroContactSearch = ({
    value,
    onChange,
}: {
    value: XeroContact | undefined
    onChange: (contact: XeroContact | null) => void
}) => {
    const [search, setSearch] = useState('')
    const [debouncedSearch] = useDebounce(search, 200)
    const [contacts] = useState<XeroContact[]>([])
    void debouncedSearch

    return (
        <Autocomplete
            value={value}
            onChange={(_e, val) => onChange(val as XeroContact | null)}
            options={contacts}
            getOptionLabel={prop('name')}
            onInputChange={(_e, val) => setSearch(val)}
        />
    )
}

const SectionHeading = ({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) => (
    <XStack
        alignItems="center"
        justifyContent="space-between"
        gap="$2"
    >
        <SizableText
            fontSize={11}
            fontWeight="500"
            letterSpacing={0.4}
            textTransform="uppercase"
            color="$colorMuted"
        >
            {children}
        </SizableText>
        {action}
    </XStack>
)

export function AgencyDrawer({
    agency,
    users,
    onClose,
}: {
    agency: {} | InternalConjunctionalAgencySchema
    users: FrontendUserSchema[]
    onClose: () => void
}) {
    const trpc = useTRPC()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const usersApi = useUsersApi()
    const showSnack = useShowSnack()
    const userName = useUserName()
    const { user } = useUserData()
    const isNew = (agency as InternalConjunctionalAgencySchema).conjunctionalAgencyId == undefined
    const canEdit = user.permissions.includes('users.edit')

    const createAgencyMutation = useMutation(trpc.conjunctionalAgencies.createConjunctionalAgency.mutationOptions())
    const updateAgencyMutation = useMutation(trpc.conjunctionalAgencies.updateConjunctionalAgency.mutationOptions())
    const deleteAgencyMutation = useMutation(trpc.conjunctionalAgencies.deleteConjunctionalAgency.mutationOptions())

    const currentUsers = isNew
        ? []
        : users
              .filter(
                  u => u.conjunctionalAgencyId === (agency as InternalConjunctionalAgencySchema).conjunctionalAgencyId,
              )
              .map(prop('userId'))

    const { control, handleSubmit, watch, setValue } = useForm<ConjunctionalAgencySchema>({
        defaultValues: isNew ? {} : { ...(agency as any), userIds: currentUsers },
        resolver: zodResolver(ConjunctionalAgencySchema),
    })

    const franchiseFull = watch('agencyCommissionBeforeHeadOfficeCommission') ?? false

    const onSubmit = handleSubmit(async data => {
        try {
            if (isNew) {
                await createAgencyMutation.mutateAsync(data)
            } else {
                await updateAgencyMutation.mutateAsync({
                    conjunctionalAgencyId: (agency as InternalConjunctionalAgencySchema).conjunctionalAgencyId,
                    data: { ...data, removedUserIds: without(data.userIds ?? [], currentUsers) },
                })
            }
            await Promise.all([conjunctionalAgenciesApi.refresh(), usersApi.refresh()])
            showSnack(`Conjunctional agency ${isNew ? 'created' : 'saved'}`, 'success')
            onClose()
        } catch (e) {
            console.error(e)
            showSnack('Error saving conjunctional agency', 'danger')
        }
    })

    return (
        <Drawer
            open
            onClose={onClose}
            title={isNew ? 'New Conjunctional Agency' : 'Edit Conjunctional Agency'}
            subtitle={watch('licenseeName') || undefined}
            width={560}
            footer={
                <>
                    {!isNew && canEdit ? (
                        <Button
                            variant="outlined"
                            theme="red"
                            icon={<Trash2 size={15} />}
                            onPress={async () => {
                                try {
                                    await deleteAgencyMutation.mutateAsync({
                                        conjunctionalAgencyId: (agency as InternalConjunctionalAgencySchema)
                                            .conjunctionalAgencyId,
                                    })
                                    await Promise.all([conjunctionalAgenciesApi.refresh(), usersApi.refresh()])
                                    showSnack('Conjunctional agency deleted', 'success')
                                    onClose()
                                } catch (e) {
                                    console.error(e)
                                    showSnack('Error deleting conjunctional agency', 'danger')
                                }
                            }}
                        >
                            Delete
                        </Button>
                    ) : (
                        <View />
                    )}
                    <XStack gap="$2">
                        <Button
                            variant="outlined"
                            onPress={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            theme="primary"
                            icon={<Check size={15} />}
                            onPress={onSubmit}
                        >
                            Save
                        </Button>
                    </XStack>
                </>
            }
        >
            <YStack gap="$5">
                {/* Identity */}
                <YStack gap="$3">
                    <SectionHeading>Identity</SectionHeading>
                    <Controller
                        name="licenseeName"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="Licensee Name"
                            />
                        )}
                    />
                    <XStack
                        gap="$3"
                        flexWrap="wrap"
                    >
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="abn"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="ABN"
                                    />
                                )}
                            />
                        </View>
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="acn"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="ACN"
                                    />
                                )}
                            />
                        </View>
                    </XStack>
                    <XStack
                        gap="$3"
                        flexWrap="wrap"
                    >
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="licenseNumber"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Licensee Number"
                                    />
                                )}
                            />
                        </View>
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="licenseExpiry"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        type="date"
                                        label="Licensee Expiry"
                                    />
                                )}
                            />
                        </View>
                    </XStack>
                </YStack>

                {/* Commission */}
                <YStack gap="$3">
                    <SectionHeading>Commission</SectionHeading>
                    <Controller
                        name="agencyCommission"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                type="number"
                                endDecorator="%"
                                label="Default Agent Commission"
                                disabled={!canEdit}
                            />
                        )}
                    />
                    {canEdit && (
                        <YStack gap="$1.5">
                            <SizableText
                                fontSize={11}
                                fontWeight="500"
                                letterSpacing={0.4}
                                textTransform="uppercase"
                                color="$colorMuted"
                            >
                                Franchise Fee Applied To Agent Commission
                            </SizableText>
                            <RadioChips<'after' | 'full'>
                                value={franchiseFull ? 'full' : 'after'}
                                onChange={val =>
                                    setValue('agencyCommissionBeforeHeadOfficeCommission', val === 'full', {
                                        shouldDirty: true,
                                    })
                                }
                                options={[
                                    { value: 'after', label: 'After Franchise Fee' },
                                    { value: 'full', label: 'Of 100%' },
                                ]}
                            />
                        </YStack>
                    )}
                </YStack>

                {/* Banking */}
                <YStack gap="$3">
                    <SectionHeading>Banking</SectionHeading>
                    <Controller
                        name="bankDetails.accountName"
                        control={control}
                        render={field => (
                            <SlotInput
                                {...field}
                                label="Account Name"
                            />
                        )}
                    />
                    <XStack
                        gap="$3"
                        flexWrap="wrap"
                    >
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="bankDetails.bsb"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="BSB"
                                    />
                                )}
                            />
                        </View>
                        <View
                            flex={1}
                            minWidth={160}
                        >
                            <Controller
                                name="bankDetails.accountNumber"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        {...field}
                                        label="Account Number"
                                    />
                                )}
                            />
                        </View>
                    </XStack>
                </YStack>

                {/* Linked users */}
                <YStack gap="$3">
                    <SectionHeading>Linked Users</SectionHeading>
                    <Controller
                        name="userIds"
                        control={control}
                        render={field => (
                            <SlotAutocomplete
                                {...field}
                                label="Users At This Agency"
                                multiple
                                options={(usersApi.data ?? []).map(prop('userId'))}
                                getOptionLabel={userName}
                                onChange={(_e, val) => field.field.onChange(val)}
                            />
                        )}
                    />
                </YStack>

                {/* Integrations */}
                <YStack gap="$3">
                    <SectionHeading>Integrations</SectionHeading>
                    <Controller
                        name="xero"
                        control={control}
                        render={field => (
                            <SlotWrapper
                                {...field}
                                label="Xero Contact"
                            >
                                <XeroContactSearch {...field.field} />
                            </SlotWrapper>
                        )}
                    />
                </YStack>
            </YStack>
        </Drawer>
    )
}
